import { Locker } from "shared/locker"
import { isSst2ReservationVisible } from "../../sst2Status"

/**
 * One normalized read of a locker's live condition, over BOTH partner shapes.
 *
 * `sstLocker` (V1) and `sst2Locker` (V2) disagree on almost every field name
 * that matters here — `_id`/`id`, `latch_status: boolean`/`"OPEN"|"CLOSED"`,
 * `doors[].door_id`/`doors[].id` — and a locker mid-migration can carry either.
 * Every consumer that needed "is it online, how many doors are free" was
 * re-deriving that inline against one shape and quietly rendering blanks for the
 * other. This module is the single place that knows the difference.
 */

export type DoorState = "free" | "occupied" | "disabled"

export type DoorVital = {
  id: string
  label: string
  number: number
  state: DoorState
  /** Latch physically open. Worth surfacing: an open latch on a free door is a fault. */
  latchOpen: boolean
  size?: string
}

export type SyncFreshness = "fresh" | "aging" | "stale" | "never"

export type LockerVitals = {
  apiVersion: "v1" | "v2"
  /** False when neither partner snapshot has ever been fetched. */
  hasSnapshot: boolean
  online: boolean | null
  /** V2 only — V1 has no equivalent, so `null` means "not reported". */
  enabled: boolean | null
  serialNumber?: string
  partnerId?: string
  doors: DoorVital[]
  doorTotal: number
  doorFree: number
  doorOccupied: number
  doorDisabled: number
  /** Share of USABLE doors that are taken, 0–100. Disabled doors are excluded. */
  occupancyPct: number
  lastSyncMs?: number
  syncAgeMs?: number
  syncFreshness: SyncFreshness
  timezone?: string
}

const HOUR = 3_600_000
export const SYNC_AGING_MS = HOUR
export const SYNC_STALE_MS = 24 * HOUR

export const syncFreshnessOf = (ageMs?: number): SyncFreshness => {
  if (ageMs === undefined) return "never"
  if (ageMs < SYNC_AGING_MS) return "fresh"
  if (ageMs < SYNC_STALE_MS) return "aging"
  return "stale"
}

const doorStateOf = (enabled: boolean, occupied: boolean): DoorState =>
  !enabled ? "disabled" : occupied ? "occupied" : "free"

/**
 * "Occupied" means the SAME thing here as on the operator dashboards — a door
 * holding a live reservation — NOT `is_available`. The page renders the
 * read-only dashboard next to these figures, and the two disagreeing (a header
 * reading "7 in use" beside a dashboard reading "0 %") is worse than either
 * definition being imperfect. V1 carries the link in `door.links`, V2 in the
 * embedded reservations; both mirror what `OsLockerDashboard*` count.
 */
const v1Doors = (locker: Locker): DoorVital[] =>
  (locker.sstLocker?.doors || []).map((d) => ({
    id: d.door_id,
    label: d.door_label || String(d.door_number),
    number: d.door_number,
    state: doorStateOf(
      !!d.is_enable,
      !!d.links?.find((l: { rel?: string }) => l?.rel === "reservation"),
    ),
    latchOpen: !!d.latch_status,
    size: d.size_description || d.size,
  }))

const v2Doors = (locker: Locker): DoorVital[] =>
  (locker.sst2Locker?.doors || []).map((d) => ({
    id: d.id,
    label: d.door_label || String(d.door_number),
    number: d.door_number,
    state: doorStateOf(!!d.is_enable, (d.reservations || []).some(isSst2ReservationVisible)),
    latchOpen: d.latch_status === "OPEN",
  }))

/**
 * @param now Injected so freshness is deterministic in tests.
 */
export const readLockerVitals = (locker: Locker, now: number): LockerVitals => {
  const apiVersion = locker.sstApiVersion === "v2" ? "v2" : "v1"
  const v2 = locker.sst2Locker
  const v1 = locker.sstLocker
  const hasSnapshot = apiVersion === "v2" ? !!v2 : !!v1

  const doors = apiVersion === "v2" ? v2Doors(locker) : v1Doors(locker)
  const doorFree = doors.filter((d) => d.state === "free").length
  const doorOccupied = doors.filter((d) => d.state === "occupied").length
  const doorDisabled = doors.filter((d) => d.state === "disabled").length
  const usable = doorFree + doorOccupied

  const syncAgeMs =
    locker.lastSstSync === undefined
      ? undefined
      : Math.max(0, now - locker.lastSstSync)

  return {
    apiVersion,
    hasSnapshot,
    online: hasSnapshot ? !!(apiVersion === "v2" ? v2?.is_online : v1?.is_online) : null,
    enabled: apiVersion === "v2" && v2 ? !!v2.is_enabled : null,
    serialNumber:
      (apiVersion === "v2" ? v2?.serial_number : v1?.serial_number) || undefined,
    partnerId: (apiVersion === "v2" ? v2?.id : v1?._id) || undefined,
    doors,
    doorTotal: doors.length,
    doorFree,
    doorOccupied,
    doorDisabled,
    occupancyPct: usable === 0 ? 0 : Math.round((doorOccupied / usable) * 100),
    lastSyncMs: locker.lastSstSync,
    syncAgeMs,
    syncFreshness: syncFreshnessOf(syncAgeMs),
    timezone: v2?.timezone || undefined,
  }
}

/** Compact "3d", "4h", "12m" — the rail has no room for "3 days ago". */
export const formatAge = (ms: number): string => {
  const m = Math.floor(ms / 60_000)
  if (m < 1) return "<1m"
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h`
  return `${Math.floor(h / 24)}d`
}
